import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { BrandLogo } from '@/components/brand/brand-logo';
import { OnboardingCsvImport } from '@/legacy/components/onboarding-csv-import';
import { Check, FileSpreadsheet, FlaskConical, PenLine, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { api } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { useSupport } from '@/lib/support-context';
import {
  DEFAULT_REMINDER_OFFSETS,
  TRADE_TYPES,
  INTERVAL_PRESETS,
  TRADE_TYPE_LABELS,
  INTERVAL_PRESET_LABELS,
  getDefaultPlanNameForTrade,
  getNicheConfig,
  isDefaultPlanNameForTrade,
  type TradeType,
} from '@maintenance-tracker/shared';
import { getOnboardingDefaultsFromNiche, readSignupNiche } from '@/legacy/lib/niche-signup';

const STEPS = ['Trade profile', 'Your agreements', 'Finish'];

type Step2Method = 'sample' | 'csv' | 'manual' | 'skip';

const STEP2_OPTIONS: Array<{
  id: Step2Method;
  title: string;
  description: string;
  icon: typeof Sparkles;
  badge?: string;
}> = [
  {
    id: 'sample',
    title: 'Explore with sample data',
    description:
      'See the full product with realistic customers and agreements. Remove everything with one click when you are ready for your real book.',
    icon: Sparkles,
    badge: 'Recommended',
  },
  {
    id: 'csv',
    title: 'Upload spreadsheet',
    description: 'Import your maintenance agreements from CSV — exports from Excel, Jobber, or QuickBooks work.',
    icon: FileSpreadsheet,
  },
  {
    id: 'manual',
    title: 'Add one manually',
    description: 'Quick entry if you only have a single plan to start with.',
    icon: PenLine,
  },
  {
    id: 'skip',
    title: 'Skip for now',
    description:
      'Add agreements later from the dashboard — import a spreadsheet from Settings → Integrations (live app connections coming soon).',
    icon: FlaskConical,
  },
];

function defaultEndDate(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const { refresh, user } = useAuth();
  const { setHint } = useSupport();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [step2Method, setStep2Method] = useState<Step2Method>('sample');
  const [csvImported, setCsvImported] = useState(false);
  const [sampleLoaded, setSampleLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nicheLabel, setNicheLabel] = useState<string | null>(null);
  const [form, setForm] = useState({
    trade_type: 'hvac' as TradeType,
    default_interval: 'annual',
    reminder_offsets: [...DEFAULT_REMINDER_OFFSETS],
    customer_name: '',
    customer_email: '',
    plan_name: getDefaultPlanNameForTrade('hvac'),
    amount: 400,
    currency: 'USD' as const,
    end_date: defaultEndDate(),
    interval_months: 12,
    send_test_to_self: true,
  });

  useEffect(() => {
    const niche = readSignupNiche();
    const defaults = getOnboardingDefaultsFromNiche(niche);
    if (niche) {
      const config = getNicheConfig(niche);
      if (config) setNicheLabel(config.trade);
    }
    if (defaults) {
      setForm((f) => ({
        ...f,
        trade_type: defaults.trade_type,
        plan_name: defaults.plan_name,
      }));
    }
  }, []);

  useEffect(() => {
    if (step === 2) setHint('import_help');
    else setHint(null);
    return () => setHint(null);
  }, [step, setHint]);

  const loadSampleData = async () => {
    setLoading(true);
    setError(null);
    try {
      await api('/api/v1/demo-data/seed', { method: 'POST', body: '{}' });
      setSampleLoaded(true);
      await refresh();
      setStep(3);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const finish = async () => {
    setLoading(true);
    setError(null);
    try {
      const includeManualAgreement = step2Method === 'manual' && form.customer_name.trim().length > 0;
      await api('/api/v1/onboarding/complete', {
        method: 'POST',
        body: JSON.stringify({
          step1: {
            trade_type: form.trade_type,
            default_interval: form.default_interval,
            reminder_offsets: form.reminder_offsets,
          },
          ...(includeManualAgreement
            ? {
                agreement: {
                  customer_name: form.customer_name.trim(),
                  customer_email: form.customer_email.trim() || undefined,
                  plan_name: form.plan_name,
                  amount: form.amount,
                  currency: form.currency,
                  start_date: new Date().toISOString().slice(0, 10),
                  end_date: form.end_date,
                  interval_months: form.interval_months,
                },
              }
            : {}),
          send_test_to_self: form.send_test_to_self,
        }),
      });
      await refresh();
      navigate({ to: '/retention' });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const continueFromStep2 = () => {
    if (step2Method === 'sample') {
      void loadSampleData();
      return;
    }
    if (step2Method === 'csv' && !csvImported) {
      setError('Upload and import your spreadsheet first, or choose another option.');
      return;
    }
    if (step2Method === 'manual' && !form.customer_name.trim()) {
      setError('Enter a customer name, or choose another option.');
      return;
    }
    setError(null);
    setStep(3);
  };

  return (
    <div className="min-h-screen bg-bg-app">
      <div className="mx-auto flex w-full max-w-2xl flex-col px-4 py-8 sm:px-6">
        <BrandLogo size="md" to="/dashboard" className="mb-5" />

        <ol className="mb-5 flex items-center">
          {STEPS.map((label, i) => {
            const n = i + 1;
            const done = n < step;
            const current = n === step;
            return (
              <li key={label} className="flex flex-1 items-center last:flex-none">
                <div className="flex items-center gap-2">
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold transition-colors ${
                      done ? 'bg-brand text-white' : current ? 'bg-brand text-white ring-4 ring-brand-subtle' : 'bg-bg-subtle text-text-muted'
                    }`}
                  >
                    {done ? <Check className="h-4 w-4" /> : n}
                  </span>
                  <span className={`hidden text-[13px] font-medium sm:block ${current || done ? 'text-text-primary' : 'text-text-muted'}`}>
                    {label}
                  </span>
                </div>
                {n < STEPS.length && <div className={`mx-3 h-px flex-1 ${done ? 'bg-brand' : 'bg-border'}`} />}
              </li>
            );
          })}
        </ol>

        <div className="rounded-2xl border border-border bg-bg-surface p-6 shadow-card">
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <h1 className="text-xl font-semibold">Tell us about your trade</h1>
                <p className="mt-1 text-sm text-text-secondary">We'll tailor defaults to how you work.</p>
                {nicheLabel && (
                  <p className="mt-2 rounded-lg bg-brand-subtle/50 px-3 py-2 text-[13px] text-text-secondary">
                    Signed up for <span className="font-medium text-text-primary">{nicheLabel}</span> — defaults are pre-filled for you.
                  </p>
                )}
              </div>
              <Select
                label="Trade type"
                value={form.trade_type}
                onChange={(e) => {
                  const trade_type = e.target.value as TradeType;
                  setForm((f) => ({
                    ...f,
                    trade_type,
                    plan_name: isDefaultPlanNameForTrade(f.plan_name)
                      ? getDefaultPlanNameForTrade(trade_type)
                      : f.plan_name,
                  }));
                }}
              >
                {TRADE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {nicheLabel && t === 'other' ? nicheLabel : TRADE_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
              <Select
                label="Default plan interval"
                value={form.default_interval}
                onChange={(e) => setForm({ ...form, default_interval: e.target.value })}
              >
                {INTERVAL_PRESETS.map((t) => (
                  <option key={t} value={t}>
                    {INTERVAL_PRESET_LABELS[t]}
                  </option>
                ))}
              </Select>
              <Button onClick={() => setStep(2)} className="w-full">
                Continue
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div>
                <h1 className="text-xl font-semibold">Bring in your agreements</h1>
                <p className="mt-1 text-sm text-text-secondary">
                  Most shops explore the product first, then import their real list or connect their field service app
                  when ready.
                </p>
              </div>

              <div className="grid gap-2">
                {STEP2_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const selected = step2Method === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => {
                        setStep2Method(option.id);
                        setError(null);
                      }}
                      className={`flex items-start gap-3 rounded-xl border p-3.5 text-left transition-colors ${
                        selected
                          ? 'border-brand bg-brand-subtle/40 ring-1 ring-brand/20'
                          : 'border-border hover:border-border-strong hover:bg-bg-subtle/50'
                      }`}
                    >
                      <div
                        className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                          selected ? 'bg-brand text-white' : 'bg-bg-subtle text-text-secondary'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium">{option.title}</span>
                          {option.badge && (
                            <span className="rounded-full bg-brand px-2 py-0.5 text-[11px] font-semibold text-white">
                              {option.badge}
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-[13px] text-text-secondary">{option.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {step2Method === 'csv' && (
                <OnboardingCsvImport
                  onBack={() => setStep2Method('sample')}
                  onSuccess={() => setCsvImported(true)}
                />
              )}

              {step2Method === 'manual' && (
                <div className="space-y-4 rounded-xl border border-border bg-bg-subtle/30 p-4">
                  <Input
                    label="Customer name"
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    required
                  />
                  <Input
                    label="Customer email (optional)"
                    type="email"
                    value={form.customer_email}
                    onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                  />
                  <Input
                    label="Plan name"
                    value={form.plan_name}
                    onChange={(e) => setForm({ ...form, plan_name: e.target.value })}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Amount"
                      type="number"
                      prefix="$"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
                    />
                    <Input
                      label="Renewal date"
                      type="date"
                      value={form.end_date}
                      onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {step2Method === 'sample' && sampleLoaded && (
                <div className="rounded-lg bg-success-subtle px-4 py-3 text-sm text-[#166534]">
                  Sample customers loaded. Continue to finish setup — you can remove all sample data with one click from
                  the dashboard banner.
                </div>
              )}

              {error && <p className="text-[13px] text-danger">{error}</p>}

              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button
                  onClick={continueFromStep2}
                  loading={loading && step2Method === 'sample'}
                  className="flex-1"
                >
                  {step2Method === 'sample'
                    ? 'Load sample workspace'
                    : step2Method === 'skip'
                      ? 'Continue without data'
                      : 'Continue'}
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div>
                <h1 className="text-xl font-semibold">You're almost set</h1>
                <p className="mt-1 text-sm text-text-secondary">
                  {step2Method === 'sample'
                    ? 'Explore the dashboard with sample customers. Remove them anytime with one click from the banner at the top — then import your list or connect your field service app in Settings.'
                    : 'When you are ready, import a spreadsheet from Settings → Integrations. Live Jobber, Housecall Pro, and QuickBooks connections are coming soon. You can optionally add a renewal page URL under Renewal page links so reminders include a link.'}
                </p>
                <p className="mt-3 rounded-lg border border-brand/15 bg-brand-subtle/30 px-3.5 py-3 text-[13px] leading-snug text-text-secondary">
                  Built for shops like yours. If something is missing or awkward, tell us — we add and change features
                  based on what owners actually need.
                </p>
              </div>
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3.5 transition-colors hover:bg-bg-subtle/60">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-border-strong accent-brand"
                  checked={form.send_test_to_self}
                  onChange={(e) => setForm({ ...form, send_test_to_self: e.target.checked })}
                />
                <span>
                  <span className="block text-sm font-medium">Send a test reminder to myself</span>
                  <span className="block text-[13px] text-text-secondary">
                    See exactly what your customers receive
                    {!user?.user.emailVerified && ' (verify your email first)'}.
                  </span>
                </span>
              </label>
              {error && <p className="text-[13px] text-danger">{error}</p>}
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setStep(2)}>
                  Back
                </Button>
                <Button onClick={finish} loading={loading} className="flex-1">
                  Go to dashboard
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
